import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";

interface StatusRes {
  status: string;
  job: { id: string; type: string; status: string; error: string | null } | null;
  proposals_count: number;
}

const STAGES = [
  { key: "analyze", label: "情報解析", desc: "ご提供いただいた情報をAIが解析しています" },
  { key: "photo", label: "写真最適化", desc: "宣材写真を最適化し魅力を引き出しています" },
  { key: "top", label: "トップページ案生成", desc: "構成・デザイン・コピーをAIが生成しています" },
  { key: "sub", label: "仕上げ", desc: "デザイン6案を整えています" },
];

// No.6 AI分析・生成中。/status をポーリングして進捗を表示。
export function GeneratingPage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusRes | null>(null);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!caseRow) return;
    const caseId = caseRow.id;
    let stopped = false;

    const poll = async () => {
      try {
        const res = await api.get<StatusRes>(`/api/cases/${caseId}/status`);
        if (stopped) return;
        setStatus(res);
        if (res.proposals_count >= 6 || res.status === "proposal") {
          if (timer.current) clearInterval(timer.current);
          navigate("/design");
        } else if (res.job?.status === "failed") {
          setFailed(true);
          if (timer.current) clearInterval(timer.current);
        }
      } catch {
        /* 一時的なエラーは次のポーリングで回復 */
      }
    };

    void poll();
    timer.current = setInterval(poll, 3000);
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [caseRow, navigate]);

  // 生成中の離脱・リロードを警告(誤操作でタブを閉じるのを防ぐ)。
  // 生成が完了(navigate で離脱)/失敗すると解除される。
  useEffect(() => {
    if (failed || loading || !caseRow) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [failed, loading, caseRow]);

  if (loading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  const jobStatus = status?.job?.status;
  const percent =
    status?.status === "proposal" || (status?.proposals_count ?? 0) >= 6
      ? 100
      : jobStatus === "processing"
        ? 65
        : jobStatus === "queued"
          ? 20
          : 10;
  const activeStage = percent >= 100 ? 4 : percent >= 60 ? 2 : percent >= 20 ? 1 : 0;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.6</span>
        <h1>AI分析・生成中</h1>
        <p className="muted">
          AIがあなたの情報をもとに、ホームページのデザイン案を作成しています。
        </p>
      </div>

      {failed ? (
        <div className="card">
          <div className="alert alert-error">
            生成中にエラーが発生しました。お手数ですが、もう一度お試しください。
          </div>
          <button className="btn btn-primary" onClick={() => navigate("/options")}>
            もう一度生成する
          </button>
        </div>
      ) : (
        <div className="card generating">
          <div className="progress-head">
            <div className="progress-percent">{percent}%</div>
            <div className="muted small">
              只今、AIがあなたのホームページを作成中です（数分かかる場合があります）
            </div>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${percent}%` }} />
          </div>
          <div className="stage-grid">
            {STAGES.map((s, i) => {
              const state = i < activeStage ? "done" : i === activeStage ? "current" : "todo";
              return (
                <div key={s.key} className={`stage ${state}`}>
                  <div className="stage-icon">{state === "done" ? "✓" : i + 1}</div>
                  <strong>{s.label}</strong>
                  <span className="muted small">{s.desc}</span>
                </div>
              );
            })}
          </div>
          <p className="muted small center">
            完了すると自動でデザイン選択画面に進みます。
          </p>
        </div>
      )}

      {/* 生成中の誤操作防止オーバーレイ(CSSのみ)。完了・失敗で消える。 */}
      {!failed && (
        <div className="generating-overlay" role="alertdialog" aria-live="assertive" aria-busy="true">
          <div className="generating-overlay-inner">
            <div className="generating-spinner" aria-hidden="true" />
            <p className="generating-overlay-msg">
              AIが生成しています。完了するまでこの画面を閉じないでください
            </p>
            <div className="generating-overlay-percent">{percent}%</div>
          </div>
        </div>
      )}
    </section>
  );
}
